import { KatalComponent, Keys } from '../../shared/base';

const Selector = {
  HEADER: 'div.button-group-header',
  FIRST_ACTION: '.button-group-header button:first-child',
  TOGGLE: '.button-group-header button.indicator',
  OPTIONS_LIST_ITEMS: 'ul.options li',
  OPTIONS_LIST: 'ul.options',
};

const preventDefaultAndStopPropagation = function(e) {
  e.preventDefault();
  e.stopPropagation();
};

/**
 * @component {kat-dropdown-button} KatalDropdownbutton A button with secondary group of of clickable options.
 * @example Basic {"variant": "primary", "size": "base", "disabled": false, "responsive": false, "options": [{"label":"Button text","action":"action1"},{"label":"Option 1","action":"action2"},{"label":"Option 2","action":"action3"}]}
 * @example Small {"variant": "primary", "size": "small", "disabled": false, "responsive": false, "options": [{"label":"Small","action":"action1"},{"label":"Option 1","action":"action2"},{"label":"Option 2","action":"action3"}]}
 * @example Disabled {"variant": "primary", "size": "small", "disabled": true, "responsive": false, "options": [{"label":"Disabled","action":"action1"},{"label":"Option 1","action":"action2"},{"label":"Option 2","action":"action3"}]}
 * @example Secondary {"variant": "secondary", "size": "base", "disabled": false, "responsive": false, "options": [{"label":"Secondary","action":"action1"},{"label":"Option 1","action":"action2"},{"label":"Option 2","action":"action3"}]}
 * @event expanded This event is fired whenever the dropdown opens or closes
 * @event action This event is fired whenever the action is clicked
 * @status Production
 */
export class KatalDropdownbutton extends KatalComponent {
  static get observedAttributes() {
    return ['options', 'variant', 'size', 'disabled', 'responsive', 'expanded'];
  }

  constructor() {
    super(KatalDropdownbutton.observedAttributes);
    this._options = [];

    // Used to prevent clicks from happening when the button is disabled
    this.onclick = function(e) {
      if (this.disabled) {
        e.stopImmediatePropagation();
      }
    };

    this.addEventListener('focusout', e => {
      const newFocus = e.relatedTarget;

      if (!this.disabled && !this.contains(newFocus) && this.expanded) {
        this.toggleExpanded();
      }
    });

    // Binding function here to being able to "removeEventListener" on each element
    this._toggleClickHandlerBinded = this._toggleClickHandler.bind(this);
    this._toggleKeydownHandlerBinded = this._toggleKeydownHandler.bind(this);
    this._firstOptionClickHandlerBinded = this._firstOptionClickHandler.bind(
      this
    );
    this._listKeydownHandlerBinded = this._listKeydownHandler.bind(this);
    this._listItemClickHandlerBinded = this._listItemClickHandler.bind(this);
  }

  /**
   * @param {json} options Array of objects [{"label":"string","action":"string"}]
   * @jsonExample - {"options": "[{"label":"Archive","action":"archive"},{"label":"Restore","action":"restore"},{"label":"Save for later","action":"postpone"}]"}
   * @required
   */
  get options() {
    return this._options;
  }

  set options(value) {
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch (e) {
        throw new Error(
          "KatalDropdown property 'options' was a string, but not valid JSON:" +
            e.toString()
        );
      }
    }

    if (Array.isArray(value)) {
      this._options = value;
      this.render(true);
    } else if (!value) {
      this._options = [];
      this.render(true);
    } else {
      throw new Error("KatalDropdown property 'options' was not an array");
    }
  }

  /**
   * @param {enum} variant Specifies the type of button to be displayed.  Defaults to primary
   * @enum {value} primary This is the default button type.
   * @enum {value} secondary some other kind button
   * @required
   */
  get variant() {
    return this.getAttribute('variant') || 'primary';
  }

  set variant(value) {
    this.setAttribute('variant', value);
  }

  /**
   * @param {enum} size Specifies the visual size of the button.  Defaults to "base"
   * @enum {value} base This is the default button size.
   * @enum {value} small This is a smaller button
   * @required
   */
  get size() {
    return this.getAttribute('size') || 'base';
  }

  set size(value) {
    this.setAttribute('size', value);
  }

  /**
   * @param {boolean} disabled If present, the button is not clickable.
   */
  get disabled() {
    return this.getBooleanAttribute('disabled');
  }

  set disabled(value) {
    this.setBooleanAttribute('disabled', value);

    if (this.disabled && this.expanded) {
      this.toggleExpanded();
    }
  }

  /**
   * @param {boolean} responsive If present, the button will adjust its appearance on smaller screens
   */
  get responsive() {
    return this.getBooleanAttribute('responsive');
  }

  set responsive(value) {
    this.setBooleanAttribute('responsive', value);
  }

  /**
   * @param {boolean} expanded This property is true if the component is currently showing its dropdown options.
   */
  get expanded() {
    return this.getBooleanAttribute('expanded');
  }

  set expanded(value) {
    this.setBooleanAttribute('expanded', value);
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  render(optionsChanged) {
    if (!this.propertiesHaveBeenUpgraded) {
      // Can't render before we are an object, and the property upgrader will trigger a render when it is done.
      return;
    }

    const currentOptionsExists = !!this.querySelector(Selector.OPTIONS_LIST);

    const querySelectorOrCreateElement = (parent, selector, elementTag) => {
      let element = parent.querySelector(selector);

      if (!element) {
        element = document.createElement(elementTag);
        parent.appendChild(element);
      }

      return element;
    };

    const headerElement = querySelectorOrCreateElement(
      this,
      Selector.HEADER,
      'div'
    );
    headerElement.setAttribute('class', `button-group-header`);

    const firstOptionButtonElement = querySelectorOrCreateElement(
      headerElement,
      Selector.FIRST_ACTION,
      'button'
    );
    firstOptionButtonElement.setAttribute('type', 'button');
    firstOptionButtonElement.dataset['action'] = this.getFirstAction();
    firstOptionButtonElement.innerHTML = document.createElement(
      'span'
    ).innerText = this.getFirstLabel();

    const indicatorButtonElement = querySelectorOrCreateElement(
      headerElement,
      Selector.TOGGLE,
      'button'
    );
    indicatorButtonElement.setAttribute('type', 'button');
    indicatorButtonElement.setAttribute('class', 'indicator');
    indicatorButtonElement.setAttribute('role', 'menuitem');
    indicatorButtonElement.setAttribute('aria-haspopup', 'true');
    indicatorButtonElement.setAttribute('aria-expanded', 'false');
    indicatorButtonElement.innerHTML =
      '<kat-icon name="chevron-down" size="small"></kat-icon>';

    // Set buttons disabled
    if (this.disabled) {
      firstOptionButtonElement.setAttribute('disabled', 'true');
      indicatorButtonElement.setAttribute('disabled', 'true');
    } else {
      firstOptionButtonElement.removeAttribute('disabled');
      indicatorButtonElement.removeAttribute('disabled');
    }

    const menuOptionsElement = querySelectorOrCreateElement(
      this,
      Selector.OPTIONS_LIST,
      'ul'
    );
    menuOptionsElement.setAttribute('class', `options`);
    menuOptionsElement.setAttribute('role', 'menu');

    if (!currentOptionsExists || optionsChanged) {
      menuOptionsElement.innerHTML = '';

      if (this.options !== '' && this.options.length > 1) {
        // Set options from options array
        this.options.slice(1).forEach(option => {
          const action = option.action || '';
          const label = option.label || '';

          const liElement = document.createElement('li');
          liElement.dataset['action'] = action;
          liElement.setAttribute('role', 'menuitem');
          liElement.setAttribute('tabindex', '0');
          liElement.innerText = label;

          menuOptionsElement.appendChild(liElement);
        });
      }
    }

    this.createEvents();
  }

  getFirstLabel() {
    return this._options.length > 0
      ? this._options[0].label || ''
      : '-- missing option';
  }

  getFirstAction() {
    return this._options.length > 0 ? this._options[0].action || '' : '';
  }

  // Respond to attribute changes.
  attributeChangedCallback(attr, oldValue, newValue) {
    // The dropdown menu is controlled with CSS and does not need a re-render
    // Primary/Secondary variants are also controlled via CSS and no not require re-rendering
    // Responsive/Non-responsive also constrolled via CSS

    const attributesToIgnore = ['expanded', 'variant', 'size', 'responsive'];
    if (attributesToIgnore.includes(attr)) {
      return;
    }

    if (oldValue !== newValue) {
      if (attr === 'options') {
        // The setter should trigger a re-render if necessary
        this[attr] = newValue;
      } else {
        // When controlling events on focus and re-render using innerHTML it loses the focus. That's why here is taking care of it.
        this.render(false);
      }
    }
  }

  _toggleClickHandler(e) {
    const innerToggle = this.querySelector(Selector.TOGGLE);

    preventDefaultAndStopPropagation(e);

    if (!this.disabled) {
      this.toggleExpanded();
    }

    if (innerToggle !== document.activeElement) {
      innerToggle.focus();
    }
  }

  _toggleKeydownHandler(e) {
    if (e.which === Keys.Escape) {
      preventDefaultAndStopPropagation(e);

      if (this.expanded) {
        this.toggleExpanded();
      }
    } else if (e.which === Keys.ArrowDown) {
      preventDefaultAndStopPropagation(e);

      if (!this.expanded) {
        this.toggleExpanded();
      }

      this._moveMenuOption(1, null);
    }
  }

  _firstOptionClickHandler() {
    if (!this.disabled) {
      this.triggerAction(this.querySelector(Selector.FIRST_ACTION));
    }
  }

  _listKeydownHandler(e) {
    if (e.which === Keys.ArrowDown) {
      preventDefaultAndStopPropagation(e);

      this._moveMenuOption(1, e.target);
    } else if (e.which === Keys.ArrowUp) {
      preventDefaultAndStopPropagation(e);

      this._moveMenuOption(-1, e.target);
    } else if (e.which === Keys.Escape) {
      preventDefaultAndStopPropagation(e);

      if (this.expanded) {
        this.toggleExpanded();
      }
    } else if (e.which === Keys.Enter) {
      e.target.click();
    }
  }

  _listItemClickHandler(e) {
    if (!this.disabled) {
      this.triggerAction(e.target);
    }
  }

  // Removing event listeners to not duplicate event listeners
  createEvents() {
    const firstOptionElement = this.querySelector(Selector.FIRST_ACTION);
    const toggleElement = this.querySelector(Selector.TOGGLE);
    const optionsListElement = this.querySelector(Selector.OPTIONS_LIST);
    const optionsListItemsElements = this.querySelectorAll(
      Selector.OPTIONS_LIST_ITEMS
    );

    if (firstOptionElement) {
      firstOptionElement.removeEventListener(
        'click',
        this._firstOptionClickHandlerBinded
      );
      firstOptionElement.addEventListener(
        'click',
        this._firstOptionClickHandlerBinded
      );
    }

    if (toggleElement) {
      toggleElement.removeEventListener(
        'click',
        this._toggleClickHandlerBinded
      );
      toggleElement.removeEventListener(
        'keydown',
        this._toggleKeydownHandlerBinded
      );
      toggleElement.addEventListener('click', this._toggleClickHandlerBinded);
      toggleElement.addEventListener(
        'keydown',
        this._toggleKeydownHandlerBinded
      );
    }

    if (optionsListElement) {
      optionsListElement.removeEventListener(
        'keydown',
        this._listKeydownHandlerBinded
      );
      optionsListElement.addEventListener(
        'keydown',
        this._listKeydownHandlerBinded
      );
    }

    if (optionsListItemsElements) {
      optionsListItemsElements.forEach(element => {
        element.removeEventListener('click', this._listItemClickHandlerBinded);
        element.addEventListener('click', this._listItemClickHandlerBinded);
      });
    }
  }

  _moveMenuOption(increment, optionsElement) {
    const items = this.querySelectorAll(Selector.OPTIONS_LIST_ITEMS);

    if (items.length === 0) {
      return;
    }

    let index = Array.from(items).indexOf(optionsElement);

    if (increment < 0 && index > 0) {
      // Up
      index -= 1;
    }

    if (increment > 0 && index < items.length - 1) {
      // Down
      index += 1;
    }

    items[index].focus();
  }

  toggleExpanded() {
    this.expanded = !this.expanded;

    const event = new CustomEvent('expanded', {
      detail: { expanded: this.expanded },
    });
    this.dispatchEvent(event);

    // Set ARIA expanded attribute
    this.querySelector(Selector.TOGGLE).setAttribute(
      'aria-expanded',
      this.expanded ? 'true' : 'false'
    );
  }

  triggerAction(element) {
    const action = element.dataset['action'];

    const event = new CustomEvent('action', {
      bubbles: true,
      detail: { action: action },
    });
    this.dispatchEvent(event);

    if (this.expanded) {
      this.toggleExpanded();
    }
  }
}

KatalComponent.register('kat-dropdown-button', KatalDropdownbutton);
